import React, { useState } from "react";
import api, { formatTL } from "@/lib/api";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { CarProfile, CurrencyCircleDollar, Sparkle, Coins, Bank, Receipt, TrendUp, TrendDown, Fire } from "@phosphor-icons/react";
import History from "@/pages/admin/History";
import OccupancyHeatmap from "@/pages/admin/OccupancyHeatmap";

const PERIODS = [
  { key: "daily",   label: "Günlük",   sub: "Bugün · saatlik" },
  { key: "weekly",  label: "Haftalık", sub: "Son 7 gün" },
  { key: "monthly", label: "Aylık",    sub: "Bu ay · günlük" },
  { key: "yearly",  label: "Yıllık",   sub: "Son 12 ay" },
];

function rangeLabel(range) {
  if (!range?.start) return "";
  try {
    const s = new Date(range.start);
    const e = new Date(range.end);
    return `${s.toLocaleDateString("tr-TR")} → ${e.toLocaleDateString("tr-TR")}`;
  } catch { return ""; }
}

function RevenuePanel() {
  const [period, setPeriod] = useState("daily");
  const { data: report, loading } = useLiveQuery(
    [TAGS.SESSIONS, TAGS.KASA, TAGS.PAYMENTS_LEDGER],
    async () => (await api.get(`/reports/revenue?period=${period}`)).data,
    { deps: [period] },
  );
  const activePeriod = PERIODS.find((p) => p.key === period);

  return (
    <div className="space-y-6">
      <div className="text-sm text-muted-foreground" data-testid="report-range-label">
        {activePeriod?.sub} · {rangeLabel(report?.range)}
      </div>

      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList className="rounded-sm">
          {PERIODS.map((p) => (
            <TabsTrigger
              key={p.key}
              value={p.key}
              data-testid={`report-${p.key}`}
              className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              {p.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={period} className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="rounded-sm border-border">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <CurrencyCircleDollar size={12} /> Otopark Geliri
                </div>
                <div className="text-2xl md:text-3xl font-bold mt-2 text-primary font-mono" data-testid="total-revenue">
                  {loading ? "..." : formatTL(report?.total_revenue)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Giriş-çıkış tahsilatı</div>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-border">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <CarProfile size={12} /> Toplam Çıkış Sayısı
                </div>
                <div className="text-2xl md:text-3xl font-bold mt-2" data-testid="total-sessions">
                  {loading ? "..." : (report?.total_sessions ?? 0)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Kapanan oturum</div>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-green-400/40 bg-green-50">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-green-800 flex items-center gap-1 font-semibold">
                  <Sparkle size={12} /> Abonelik Tahsilatları
                </div>
                <div className="text-2xl md:text-3xl font-bold mt-2 text-green-700 font-mono" data-testid="total-subscription-revenue">
                  {loading ? "..." : formatTL(report?.subscription_payments?.amount)}
                </div>
                <div className="text-[10px] text-green-800/80 mt-1">
                  {loading ? "..." : (report?.subscription_payments?.count ?? 0)} abonelik ödemesi
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-primary/40 bg-primary/5">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-primary flex items-center gap-1 font-semibold">
                  <Coins size={12} /> Genel Toplam
                </div>
                <div className="text-2xl md:text-3xl font-bold mt-2 text-primary font-mono" data-testid="grand-total-revenue">
                  {loading ? "..." : formatTL(report?.grand_total)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Otopark + Abonelik</div>
              </CardContent>
            </Card>
          </div>

          {/* Abonelik ödeme yöntemi kırılımı */}
          {(report?.subscription_payments?.count ?? 0) > 0 && (
            <Card className="rounded-sm border-green-200 bg-green-50/40">
              <CardContent className="p-5">
                <h3 className="text-xs uppercase tracking-widest text-green-800 font-semibold mb-3">
                  Abonelik Ödemeleri · Ödeme Yöntemine Göre
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {["cash", "card", "bank_transfer"].map((m) => {
                    const b = report?.subscription_payments?.by_method?.[m] || { count: 0, amount: 0 };
                    const label = m === "cash" ? "Nakit" : m === "card" ? "Kart" : "EFT / Havale";
                    return (
                      <div key={m} className="p-3 rounded-sm bg-white border border-green-200">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                        <div className="text-lg font-mono font-bold text-green-700 mt-1" data-testid={`sub-payments-${m}`}>
                          {formatTL(b.amount)}
                        </div>
                        <div className="text-[10px] text-muted-foreground">{b.count} ödeme</div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* NET GELİR (Gelir − Gider) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="rounded-sm border-emerald-500/40 bg-emerald-50">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-emerald-800 flex items-center gap-1 font-semibold">
                  <TrendUp size={12} /> Net Kâr (Gelir − Gider)
                </div>
                <div className="text-3xl font-bold mt-2 text-emerald-700 font-mono" data-testid="net-income">
                  {loading ? "..." : formatTL(report?.net_income)}
                </div>
                <div className="text-[10px] text-emerald-800/70 mt-1 font-mono">
                  Genel Toplam {formatTL(report?.grand_total)} − Gider {formatTL(report?.expenses?.amount || 0)}
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-blue-400/40 bg-blue-50">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-blue-800 flex items-center gap-1 font-semibold">
                  <Bank size={12} /> Banka Aktarımları
                </div>
                <div className="text-3xl font-bold mt-2 text-blue-700 font-mono" data-testid="bank-transfers-total">
                  {loading ? "..." : formatTL(report?.bank_transfers?.amount)}
                </div>
                <div className="text-[10px] text-blue-800/70 mt-1">
                  {loading ? "..." : (report?.bank_transfers?.count ?? 0)} aktarım · Nakit {formatTL(report?.bank_transfers?.by_method?.cash?.amount || 0)} · Kart {formatTL(report?.bank_transfers?.by_method?.card?.amount || 0)}
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-destructive/40 bg-destructive/5">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-destructive flex items-center gap-1 font-semibold">
                  <TrendDown size={12} /> Toplam Giderler
                </div>
                <div className="text-3xl font-bold mt-2 text-destructive font-mono" data-testid="expenses-total-report">
                  -{loading ? "..." : formatTL(report?.expenses?.amount)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  {loading ? "..." : (report?.expenses?.count ?? 0)} kayıt · Nakit {formatTL(report?.expenses?.by_method?.cash?.amount || 0)} · Kart {formatTL(report?.expenses?.by_method?.card?.amount || 0)} · Banka {formatTL(report?.expenses?.by_method?.bank_transfer?.amount || 0)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Kategori bazlı gider kırılımı */}
          {(report?.expenses?.count ?? 0) > 0 && (
            <Card className="rounded-sm border-destructive/20 bg-destructive/[0.02]">
              <CardContent className="p-5">
                <h3 className="text-xs uppercase tracking-widest text-destructive font-semibold mb-3 flex items-center gap-2">
                  <Receipt size={14} /> Giderler · Kategoriye Göre
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {(report?.expenses?.by_category || []).map((c) => (
                    <div key={c.category} className="p-3 rounded-sm bg-white border border-destructive/20">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.category}</div>
                      <div className="text-lg font-mono font-bold text-destructive mt-1">-{formatTL(c.amount)}</div>
                      <div className="text-[10px] text-muted-foreground">{c.count} kayıt</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="rounded-sm border-border">
            <CardContent className="p-6">
              <h3 className="font-semibold uppercase tracking-widest text-sm mb-4">
                Gelir (Otoparka göre)
              </h3>
              <div className="h-72">
                {(report?.data?.length ?? 0) === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                    Bu dönemde gelir kaydı yok
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report?.data || []}>
                      <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "4px", fontSize: "12px" }} />
                      <Legend wrapperStyle={{ fontSize: "12px" }} />
                      <Bar dataKey="ust_kat" fill="hsl(var(--primary))" name="Üst Kat" />
                      <Bar dataKey="alt_kat" fill="hsl(var(--foreground))" name="Alt Kat" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function Reports() {
  const [tab, setTab] = useState("revenue");

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Analiz</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Raporlar</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="revenue" data-testid="reports-tab-revenue"
                       className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Gelir Raporu
          </TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="reports-tab-heatmap"
                       className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Fire size={14} className="mr-1.5" /> Doluluk Isı Haritası
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="reports-tab-history"
                       className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Oturum Geçmişi
          </TabsTrigger>
        </TabsList>
        <TabsContent value="revenue" className="mt-6">
          <RevenuePanel />
        </TabsContent>
        <TabsContent value="heatmap" className="mt-6">
          <OccupancyHeatmap />
        </TabsContent>
        <TabsContent value="history" className="mt-6">
          <History />
        </TabsContent>
      </Tabs>
    </div>
  );
}
